import type { GetApiV1ThreeLetterCodePartnerAdminsUsers200 } from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date/formatDate';
import { convertDateTime } from '~/utils/dateTime';
import type { UserListPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof UserListPresentation>;

type Args = {
  /** API から取得した利用者一覧データ */
  usersData: GetApiV1ThreeLetterCodePartnerAdminsUsers200;
  /**
   * 検索条件が適用されているか
   *
   * true のとき 0 件でも Empty 画面を表示しない（PEN-309 対応パターン）
   */
  isFiltered: boolean;
};

type IConvertToPresentationProps = (params: Args) => {
  rows: PresentationProps['rows'];
  /** 利用者が 0 件かどうか */
  isEmpty: boolean;
};

/**
 * API 型（snake_case）を UserList Presentation の props 型（camelCase）に変換する。
 *
 * - 出力型は `UserListPresentation` の Props から導出し、型乖離を防ぐ
 * - isEmpty フラグを導出して Container 層での表示出し分けに使用する
 * - 検索条件あり × 0 件は Empty 画面を表示しない（PEN-309 対応パターン）
 * - ページネーション（pager）の組み立ては Container 層（generatePagerProps）が担う
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  usersData,
  isFiltered,
}) => {
  const users = usersData.users;

  const rows: PresentationProps['rows'] = users.map(user => ({
    id: user.id,
    clientName: user.client.name,
    name: user.full_name,
    isActivated: user.is_activated,
    activationCode: user.activation_code ?? undefined,
    email: user.email ?? '',
    phoneNumber: user.phone_number ?? undefined,
    billableFrom: user.billable_from
      ? formatDate({ date: new Date(`${user.billable_from}T00:00:00`), formatType: 'yyyy/M/d' })
      : '-',
    createdAt: convertDateTime(user.created_at),
    updatedAt: formatDate({ date: new Date(user.updated_at * 1000), formatType: 'yyyy/M/d H:mm' }),
  }));

  // 検索条件あり × 0 件は Empty 画面を表示しない（PEN-309 対応パターン）
  const isEmpty = users.length === 0 && !isFiltered;

  return { rows, isEmpty };
};
